import { useCallback, useMemo } from 'react'
import { usePortfolioData } from '../utilityPanel/PortfolioDataProvider'

// Thin facade over the chart-instance setters exposed through
// PortfolioDataProvider. Mutations route through useExplorerConfig
// and mark the view dirty.
//
// Instance model (config v8+):
//   defaultCharts: Array<{ id, catalogId?, toolId? }>
//
// `instances` is the raw array. `addChart({ catalogId? toolId? })`
// appends a new instance (caller doesn't need to know about ids).
// `removeChart(instanceId)` removes by stable instance id, NOT by
// catalogId -- duplicates of the same preset are independent rows.
// `instanceCountByCatalog(catalogId)` is for informational badges
// in the catalog menu (e.g. "Allocation by MV  •  2"); doesn't gate
// the add button anymore.
export function useChartCatalogActions () {
  const { config, addDefaultChart, removeDefaultChart, updateDefaultChart, setDefaultCharts } = usePortfolioData()

  // Memoize so the `||` fallback doesn't produce a new empty array
  // every render (which would re-fire downstream useMemo / useCallback
  // deps unnecessarily, including `instanceCountByCatalog` below).
  const instances = useMemo(() => config?.defaultCharts || [], [config?.defaultCharts])

  const addChart = useCallback((spec) => {
    if (!addDefaultChart) return
    // Back-compat for legacy callers that passed a bare catalogId.
    const normalized = typeof spec === 'string' ? { catalogId: spec } : spec
    if (!normalized || (!normalized.catalogId && !normalized.toolId)) return
    addDefaultChart(normalized)
  }, [addDefaultChart])

  const removeChart = useCallback((instanceId) => {
    if (!instanceId || !removeDefaultChart) return
    removeDefaultChart(instanceId)
  }, [removeDefaultChart])

  // Patches an instance. `patch.payload` shallow-merges into the
  // instance's existing payload, so callers can write `{ payload:
  // { chartType: 'bar' } }` without rebuilding everything else.
  // Tools normally call this via the `onPayloadChange` callback
  // threaded down from ChartStack.
  const updateChart = useCallback((instanceId, patch) => {
    if (!instanceId || !patch || !updateDefaultChart) return
    updateDefaultChart(instanceId, patch)
  }, [updateDefaultChart])

  const instanceCountByCatalog = useCallback((catalogId) => (
    instances.filter(i => i.catalogId === catalogId).length
  ), [instances])

  return {
    instances,
    addChart,
    removeChart,
    updateChart,
    setInstances: setDefaultCharts,
    instanceCountByCatalog
  }
}
